import BaseComponent from "../../BaseComponent";
import { Dropdown } from "bootstrap";
import normalizeString from "../../../helpers/normalizeString";
import shuffleArray from "../../../helpers/shuffleArray";

export default class AnalyzeDiscussions extends BaseComponent {

    private static readonly QUESTIONS_COUNT = 4;

    private maxSelectedItemsCount: number;

    private $questions: JQuery<HTMLElement>[] = [];
    private questionsPointer = 0;
    private removeSelectListeners: (() => void) | null = null;

    init(): void {
        this.maxSelectedItemsCount = Number(this.$component.data('maxSelectedItemsCount') ?? 0);

        this.initSelectFunctions();
        this.$questions = this.prepareQuestionSelection();

        const $questionTextarea = <JQuery<HTMLTextAreaElement>>this.$component.find('textarea[name="question"]');
        const $send = this.$component.find('button[name="send"]');

        // Change height of textarea by its content
        $questionTextarea.on('input', () => {
            $questionTextarea[0].style.height = '5px';
            $questionTextarea[0].style.height = $questionTextarea[0].scrollHeight + 'px';

            $send.prop('disabled', !String($questionTextarea.val()).trim());
        });
        $questionTextarea.trigger('input');

        // Send question by hitting Enter, create new line with Shift+Enter
        $questionTextarea.on('keydown', e => {
            if (e.key === 'Enter' && !e.shiftKey && String($questionTextarea.val()).trim()) {
                e.preventDefault();

                $send.click();
                $questionTextarea.prop('disabled', true);
            }
        });

        // Disallow send form by send button when textarea is empty
        $send.closest('form').on('submit', e => {
            // @ts-ignore
            if ($(e.originalEvent.submitter).is('button[name="send"]') && !String($questionTextarea.val()).trim()) {
                e.preventDefault();
                $questionTextarea.focus();
            }
        })


        this.$component.find('[data-refresh-questions]').on('click', () => {
            this.questionsPointer += AnalyzeDiscussions.QUESTIONS_COUNT;
            this.updateQuestionVisibility();
        });
        this.updateQuestionVisibility();

        this.$component.find('input[data-predefined-question-group]').on('click', e => {
            e.preventDefault();
            const text = $(e.target).val();
            $questionTextarea.val(text);
            $questionTextarea.trigger('input');
        })
    }

    public beforeRemove(): void {
        this.removeSelectListeners?.();
        this.removeSelectListeners = null;
    }

    private initSelectFunctions() {
        const $groupCheckboxes = this.$component.find('.select-group');
        const $itemCheckboxes = this.$component.find('.item-checkbox');
        const $contextInput = this.$component.find('select#context-input');
        const $contextGroups = this.$component.find('.context-group');
        const $selectedItemsContainer = this.$component.find('#selected-items');
        const $searchInput = this.$component.find('#search-input');

        const dropdown = new Dropdown($searchInput[0]);

        $searchInput.prop('disabled', false);

        const syncContextInput = () => {
            const checkedValues = $itemCheckboxes
                .filter(':checked')
                .map((_, checkbox) => String($(checkbox).val()))
                .get();
            $contextInput.val(checkedValues);
        };

        // Group → toggle all items in the group
        $groupCheckboxes.on('change', (e) => {
            const $target = $(e.target);
            const group = $target.data('group');
            this.$component
                .find(`.item-checkbox[data-group="${group}"]`)
                .prop('checked', $target.prop('checked'));
            updateSelectedItems();
        });

        // Item → update group checkbox state + selection list
        $itemCheckboxes.on('change', (e) => {
            const $target = $(e.target);
            const group = $target.data('group');
            const $groupCheckbox = this.$component.find(`.select-group[data-group="${group}"]`);
            const $groupItems = this.$component.find(`.item-checkbox[data-group="${group}"]`);
            const allChecked = $groupItems.toArray().every(item => $(item).prop('checked'));
            $groupCheckbox.prop('checked', allChecked);
            updateSelectedItems();
        });

        const filterItems = () => {
            const query = normalizeString(String($searchInput.val() ?? ''));
            $contextGroups.each((_, group) => {
                const $group = $(group);
                const $items = $group.find('.context-item');
                let groupHasVisibleItems = false;

                $items.each((_, item) => {
                    const $item = $(item);
                    const $title = $item.find('[data-context-title]');
                    const itemKeywords = normalizeString($title.length
                        ? $title.text()
                        : String($item.attr('data-keywords') ?? ''));
                    const isVisible = itemKeywords.includes(query);

                    $item.css('display', isVisible ? 'block' : 'none');
                    if (isVisible) {
                        groupHasVisibleItems = true;
                    }
                });

                const $groupCheckbox = $group.find('.group-checkbox');
                if ($groupCheckbox.length) {
                    if (query) {
                        $groupCheckbox.hide();
                    } else {
                        $groupCheckbox.show();
                    }
                }

                $group.css('display', groupHasVisibleItems || !query ? 'block' : 'none');
            });
        };

        const updateSelectedItems = () => {
            $selectedItemsContainer.empty();

            let selectedCount = 0;
            $itemCheckboxes.each((_, checkbox) => {
                const $checkbox = $(checkbox);
                if ($checkbox.prop('checked')) {
                    selectedCount++;
                    const $button = $('<button>')
                        .attr('type', 'button')
                        .addClass('btn btn-outline-secondary btn-sm')
                        .text($checkbox.next('label').text().trim())
                        .append($('<i>').addClass('bi bi-x ms-2'))
                        .on('click', () => {
                            $checkbox.prop('checked', false);
                            $checkbox.trigger('change');
                        });

                    $selectedItemsContainer.append($button);
                }
            });

            if (this.maxSelectedItemsCount && selectedCount > this.maxSelectedItemsCount) {
                $selectedItemsContainer.prepend($(
                    '<div class="alert alert-warning w-100">'
                        + this.translator.translate('too_many_items').replace('%count%', '' + this.maxSelectedItemsCount)
                    + '</div>'
                ));
            }

            const placeholder = selectedCount
                ? this.translator.translate('selected').replace('{0}', '' + selectedCount)
                : this.translator.translate('select_context');
            $searchInput.attr('placeholder', placeholder);

            syncContextInput();
        };

        $searchInput.on('input', filterItems);
        this.$component.on('content-translation-state-changed.analyzeDiscussions', () => {
            filterItems();
            updateSelectedItems();
        });

        // Initial population (handles preselected items rendered checked)
        filterItems();
        updateSelectedItems();

        this.$component.find('.dropdown-menu').on('click', (event) => {
            event.stopPropagation();
        });

        // Show the dropdown on input focus
        $searchInput.on("focus", () => {
            dropdown.show();
        });

        // Close dropdown if clicked outside
        const onDocumentClick = (event: JQuery.ClickEvent) => {
            if (!$searchInput.is(<Element><unknown>event.target) && $searchInput.has(<Element><unknown>event.target).length === 0) {
                dropdown.hide();
            }
        };
        $(document).on('click.analyzeDiscussions', onDocumentClick);

        this.removeSelectListeners = () => {
            this.$component.off('content-translation-state-changed.analyzeDiscussions');
            $(document).off('click.analyzeDiscussions', onDocumentClick);
            dropdown.dispose();
        };
    }

    /**
     * Simple shuffle question elements
     */
    private prepareQuestionSelection(): JQuery<HTMLElement>[] {
        const questionEls = Array.from(this.$component.find('[data-predefined-question-group]'));
        const $questions = questionEls.map(item => $(item));
        return shuffleArray($questions);
    }

    private updateQuestionVisibility(): void {
        const visibleIndexes = new Array(AnalyzeDiscussions.QUESTIONS_COUNT)
            .fill(0)
            .map( (v, i) => (this.questionsPointer+i) % this.$questions.length )
        ;

        this.$questions.forEach(($el, i) => {
            const visibleIndex = visibleIndexes.findIndex(vi => vi === i);
            if (visibleIndex > -1) {
                $el.show(0);
            } else {
                $el.hide(0);
            }
        })

    }
}
